import { GetAllMentionsQuery } from '../../domain/entities/RedditMention';
import { PaginationParams } from '../../domain/common/PaginatedResult';
import type { PendingMentionsQuery } from '../../infrastructure/repositories/CustomerMentionRepository';

/**
 * Query Keys Factory
 *
 * Centralized query key management for React Query.
 * This ensures consistent cache keys and makes invalidation easier.
 *
 * Pattern:
 * - ['entity'] - matches ALL queries for that entity
 * - ['entity', 'list'] - matches all list queries
 * - ['entity', 'list', params] - matches specific list query with params
 * - ['entity', 'detail', id] - matches specific detail query
 */

export const queryKeys = {
  /**
   * Mention query keys
   */
  mentions: {
    /**
     * Matches all mention queries
     */
    all: ['mentions'] as const,

    /**
     * Matches all mention list queries
     */
    lists: (): readonly ['mentions', 'list'] => [...queryKeys.mentions.all, 'list'] as const,

    /**
     * Matches specific mention list query with filters
     */
    list: (query?: GetAllMentionsQuery): readonly ['mentions', 'list', GetAllMentionsQuery | undefined] =>
      [...queryKeys.mentions.lists(), query] as const,

    /**
     * Matches specific mention detail
     */
    detail: (id: string): readonly ['mentions', 'detail', string] =>
      [...queryKeys.mentions.all, 'detail', id] as const
  },

  /**
   * Business query keys
   */
  businesses: {
    /**
     * Matches all business queries
     */
    all: ['businesses'] as const,

    /**
     * Matches all business list queries
     */
    lists: (): readonly ['businesses', 'list'] => [...queryKeys.businesses.all, 'list'] as const,

    /**
     * Matches specific business list query with pagination, filters, and sorting
     * SESSION_320: Added sortBy and sortDescending
     */
    list: (
      params?: PaginationParams,
      searchTerm?: string,
      category?: string,
      regionId?: string,
      subAreaId?: string,
      sortBy?: string,
      sortDescending?: boolean,
      isActive?: boolean
    ): readonly ['businesses', 'list', PaginationParams | undefined, string | undefined, string | undefined, string | undefined, string | undefined, string | undefined, boolean | undefined, boolean | undefined] =>
      [...queryKeys.businesses.lists(), params, searchTerm, category, regionId, subAreaId, sortBy, sortDescending, isActive] as const,

    /**
     * Matches all search queries
     */
    searches: (): readonly ['businesses', 'search'] => [...queryKeys.businesses.all, 'search'] as const,

    /**
     * Matches specific search query
     * SESSION_137: Added regionId to cache key for region-filtered searches
     */
    search: (query: string, limit?: number, regionId?: string): readonly ['businesses', 'search', string, number | undefined, string | undefined] =>
      [...queryKeys.businesses.searches(), query, limit, regionId] as const,

    /**
     * Matches paginated search query
     * Sprint 037: Added for paginated business search with relevance scoring
     */
    searchPaginated: (
      query: string,
      pageNumber: number,
      pageSize: number,
      regionId?: string
    ): readonly ['businesses', 'search', 'paginated', string, number, number, string | undefined] =>
      [...queryKeys.businesses.searches(), 'paginated', query, pageNumber, pageSize, regionId] as const,

    /**
     * Matches specific business detail
     */
    detail: (id: string): readonly ['businesses', 'detail', string] =>
      [...queryKeys.businesses.all, 'detail', id] as const,

    /**
     * Matches business summary by ID query (public - for registration pre-population)
     * SESSION_276: Added for registration URL pre-population
     */
    summaryById: (id: string): readonly ['businesses', 'summary', string] =>
      [...queryKeys.businesses.all, 'summary', id] as const,

    /**
     * Matches businesses by company ID query (public - for fast registration pre-population)
     * SESSION_276: Added for fast company lookup
     */
    byCompanyId: (companyId: string): readonly ['businesses', 'byCompany', string] =>
      [...queryKeys.businesses.all, 'byCompany', companyId] as const
  },

  /**
   * Region query keys
   */
  regions: {
    /**
     * Matches all region queries
     */
    all: ['regions'] as const,

    /**
     * Matches all region list queries
     */
    lists: (): readonly ['regions', 'list'] => [...queryKeys.regions.all, 'list'] as const,

    /**
     * Matches specific region list query with pagination
     */
    list: (params?: PaginationParams): readonly ['regions', 'list', PaginationParams | undefined] =>
      [...queryKeys.regions.lists(), params] as const,

    /**
     * Matches specific region detail
     */
    detail: (id: string): readonly ['regions', 'detail', string] =>
      [...queryKeys.regions.all, 'detail', id] as const,

    /**
     * Matches region dropdown query (lightweight, anonymous)
     * Sprint 032: Separate cache key for minimal dropdown data
     */
    dropdown: (): readonly ['regions', 'dropdown'] =>
      [...queryKeys.regions.all, 'dropdown'] as const
  },

  /**
   * User query keys
   */
  users: {
    /**
     * Matches all user queries
     */
    all: ['users'] as const,

    /**
     * Matches all user list queries
     */
    lists: (): readonly ['users', 'list'] => [...queryKeys.users.all, 'list'] as const,

    /**
     * Matches specific user list query with pagination
     */
    list: (params?: PaginationParams): readonly ['users', 'list', PaginationParams | undefined] =>
      [...queryKeys.users.lists(), params] as const,

    /**
     * Matches specific user detail
     */
    detail: (id: string): readonly ['users', 'detail', string] => [...queryKeys.users.all, 'detail', id] as const,

    /**
     * Matches current user query
     */
    current: (): readonly ['users', 'current'] => [...queryKeys.users.all, 'current'] as const
  },

  /**
   * SubArea query keys
   */
  subAreas: {
    /**
     * Matches all SubArea queries
     */
    all: ['subAreas'] as const,

    /**
     * Matches all SubArea list queries
     */
    lists: (): readonly ['subAreas', 'list'] => [...queryKeys.subAreas.all, 'list'] as const,

    /**
     * Matches SubAreas for a specific region
     */
    byRegion: (regionId: string): readonly ['subAreas', 'list', 'region', string] =>
      [...queryKeys.subAreas.lists(), 'region', regionId] as const,

    /**
     * Matches specific SubArea detail
     */
    detail: (id: string): readonly ['subAreas', 'detail', string] =>
      [...queryKeys.subAreas.all, 'detail', id] as const
  },

  /**
   * Reddit Crawl query keys (Admin Operations)
   */
  redditCrawl: {
    /**
     * Matches all Reddit crawl queries
     */
    all: ['redditCrawl'] as const,

    /**
     * Matches Reddit crawl status query
     */
    status: (): readonly ['redditCrawl', 'status'] => [...queryKeys.redditCrawl.all, 'status'] as const
  },

  /**
   * Admin Dashboard query keys (Admin Operations)
   */
  dashboard: {
    /**
     * Matches all dashboard queries
     */
    all: ['dashboard'] as const,

    /**
     * Matches dashboard statistics query
     */
    stats: (): readonly ['dashboard', 'stats'] => [...queryKeys.dashboard.all, 'stats'] as const,

    /**
     * Matches rate limit status query
     */
    rateLimit: (): readonly ['dashboard', 'rateLimit'] => [...queryKeys.dashboard.all, 'rateLimit'] as const
  },

  /**
   * Subscription query keys
   * SESSION_185: Frontend Stripe Integration
   */
  subscriptions: {
    /**
     * Matches all subscription queries
     */
    all: ['subscriptions'] as const,

    /**
     * Matches current user's subscription query
     */
    my: (): readonly ['subscriptions', 'my'] => [...queryKeys.subscriptions.all, 'my'] as const,

    /**
     * Matches subscription access query (for feature gating)
     */
    access: (businessId?: string): readonly ['subscriptions', 'access', string | undefined] =>
      [...queryKeys.subscriptions.all, 'access', businessId] as const
  },

  /**
   * Onboarding query keys
   * SESSION_199: Onboarding Frontend Step 1
   */
  onboarding: {
    /**
     * Matches all onboarding queries
     */
    all: ['onboarding'] as const,

    /**
     * Matches onboarding status query
     */
    status: (): readonly ['onboarding', 'status'] =>
      [...queryKeys.onboarding.all, 'status'] as const
  },

  /**
   * Customer mention query keys
   * Sprint 037 Session 9: Customer self-confirmation UI
   */
  customerMentions: {
    /**
     * Matches all customer mention queries
     */
    all: ['customerMentions'] as const,

    /**
     * Matches pending customer mentions query
     */
    pending: (
      businessId: string | null,
      query?: PendingMentionsQuery
    ): readonly ['customerMentions', 'pending', string | null, PendingMentionsQuery | undefined] =>
      [...queryKeys.customerMentions.all, 'pending', businessId, query] as const
  }
} as const;

/**
 * Type-safe query key helper
 *
 * Usage:
 * - queryClient.invalidateQueries({ queryKey: queryKeys.mentions.all }) - invalidates ALL mention queries
 * - queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() }) - invalidates all list queries
 * - queryClient.invalidateQueries({ queryKey: queryKeys.mentions.list(query) }) - invalidates specific query
 */
